import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import documentoService from '../services/documentoService';
import api from '../services/api';
import { PERMISOS_MODULOS } from '../utils/permissions';
import ConfirmModal from './ConfirmModal';
import '../styles/EditarDocumentoModal.css';
import { LuX, LuSave, LuFileImage, LuTrash2 } from 'react-icons/lu';

const EditarDocumentoModal = ({ documento, onClose, onSuccess }) => {
  const { usuario } = useAuth();
  const toast = useToast();

  const [formData, setFormData] = useState({
    TipoRegistro: '',
    Fecha: '',
    Hora: '',
    Local: '',
    CentroCosto: '',
    Motivo: '',
    TipoDoc: '',
    NumDoc: '',
    Monto: '',
    Glosa: ''
  });

  const [catalogos, setCatalogos] = useState({
    locales: [],
    grupos: [],
    motivos: [],
    tiposDoc: []
  });

  const [archivos, setArchivos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmEliminarImagen, setConfirmEliminarImagen] = useState(null);

  useEffect(() => {
    if (documento) {
      setFormData({
        TipoRegistro: documento.TipoRegistro || '',
        Fecha: documento.Fecha || '',
        Hora: documento.Hora || '',
        Local: documento.Local || '',
        CentroCosto: documento.GrupoId || '',
        Motivo: documento.Motivo || '',
        TipoDoc: documento.TipoDoc || '',
        NumDoc: documento.NumDoc || 0,
        Monto: documento.Monto || 0,
        Glosa: documento.Glosa || ''
      });
    }
  }, [documento]);

  useEffect(() => {
    cargarCatalogos();
  }, []);

  const cargarCatalogos = async () => {
    try {
      const response = await api.get('/catalogos');
      if (response.data.success) {
        setCatalogos({
          locales: response.data.data.locales,
          grupos: response.data.data.grupos,
          motivos: response.data.data.motivos,
          tiposDoc: response.data.data.tiposDoc
        });
      }
    } catch (error) {
      toast.error('Error al cargar los catálogos');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setArchivos(Array.from(e.target.files));
  };

  const handleEliminarImagen = (imagenId) => {
    setConfirmEliminarImagen(imagenId);
  };

  const doEliminarImagen = async () => {
    const imagenId = confirmEliminarImagen;
    setConfirmEliminarImagen(null);
    try {
      await documentoService.eliminarImagenDocumento(documento.id, imagenId);
      toast.success('Imagen eliminada exitosamente');
      onSuccess();
    } catch (error) {
      toast.error(error.message || 'Error al eliminar imagen');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!PERMISOS_MODULOS.PUEDE_EDITAR_DOCUMENTOS(usuario)) {
      toast.error('Sin permisos para editar documentos');
      return;
    }
    if (!formData.TipoRegistro) {
      toast.warning('El tipo de registro es requerido');
      return;
    }
    if (!formData.Fecha) {
      toast.warning('La fecha es requerida');
      return;
    }
    if (!formData.Hora) {
      toast.warning('La hora es requerida');
      return;
    }
    if (!formData.Monto || formData.Monto <= 0) {
      toast.warning('El monto debe ser mayor a 0');
      return;
    }
    if (!formData.Local) {
      toast.warning('El local es requerido');
      return;
    }
    if (!formData.Motivo) {
      toast.warning('El motivo es requerido');
      return;
    }
    if (!formData.TipoDoc) {
      toast.warning('El tipo de documento es requerido');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('TipoRegistro', formData.TipoRegistro);
      formDataToSend.append('Fecha', formData.Fecha);
      formDataToSend.append('Hora', formData.Hora);
      formDataToSend.append('Local', formData.Local);
      formDataToSend.append('Motivo', formData.Motivo);
      formDataToSend.append('TipoDoc', formData.TipoDoc);
      formDataToSend.append('Monto', formData.Monto);

      if (formData.CentroCosto) formDataToSend.append('CentroCosto', formData.CentroCosto);
      if (formData.NumDoc) formDataToSend.append('NumDoc', formData.NumDoc);
      if (formData.Glosa) formDataToSend.append('Glosa', formData.Glosa);

      archivos.forEach(archivo => formDataToSend.append('archivos', archivo));

      const response = await documentoService.editarDocumento(documento.id, formDataToSend);

      if (response.success) {
        toast.success('Documento actualizado exitosamente');
        onSuccess();
        onClose();
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        const errores = error.response.data.errors.map(e => e.msg).join(', ');
        toast.error(`Errores de validación: ${errores}`);
      } else {
        toast.error(error.message || error.response?.data?.message || 'Error al actualizar documento');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!documento) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Editar Documento #{documento.id}</h2>
          <button className="modal-close" onClick={onClose}>
            <LuX />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {/* Tipo de Registro */}
          <div className="form-row">
            <div className="form-group">
              <label>Tipo de Registro *</label>
              <select name="TipoRegistro" value={formData.TipoRegistro} onChange={handleChange} required>
                <option value="">Seleccione...</option>
                <option value="Ingreso">Ingreso</option>
                <option value="Gasto">Gasto</option>
              </select>
            </div>

            <div className="form-group">
              <label>Monto *</label>
              <input
                type="number"
                name="Monto"
                value={formData.Monto}
                onChange={handleChange}
                min="1"
                step="1"
                required
              />
            </div>
          </div>

          {/* Fecha y Hora */}
          <div className="form-row">
            <div className="form-group">
              <label>Fecha *</label>
              <input type="date" name="Fecha" value={formData.Fecha} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Hora *</label>
              <input type="time" name="Hora" value={formData.Hora} onChange={handleChange} required />
            </div>
          </div>

          {/* Local y Grupo */}
          <div className="form-row">
            <div className="form-group">
              <label>Local *</label>
              <select name="Local" value={formData.Local} onChange={handleChange} required>
                <option value="">Seleccione...</option>
                {catalogos.locales.map(local => (
                  <option key={local.id} value={local.CodLocal}>{local.Nombre}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Grupo</label>
              <select name="CentroCosto" value={formData.CentroCosto} onChange={handleChange}>
                <option value="">Seleccione...</option>
                {catalogos.grupos.map(grupo => (
                  <option key={grupo.id} value={grupo.id}>{grupo.Descripcion || grupo.Nombre}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Motivo y Tipo Documento */}
          <div className="form-row">
            <div className="form-group">
              <label>Motivo *</label>
              <select name="Motivo" value={formData.Motivo} onChange={handleChange} required>
                <option value="">Seleccione...</option>
                {catalogos.motivos.map(motivo => (
                  <option key={motivo.Id} value={motivo.CodMotivo}>{motivo.NombreMotivo}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Tipo Documento *</label>
              <select name="TipoDoc" value={formData.TipoDoc} onChange={handleChange} required>
                <option value="">Seleccione...</option>
                {catalogos.tiposDoc.map(tipo => (
                  <option key={tipo.id} value={tipo.CodDoc}>{tipo.Nombre}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Número */}
          <div className="form-row">
            <div className="form-group">
              <label>Número de Documento</label>
              <input type="number" name="NumDoc" value={formData.NumDoc} onChange={handleChange} min="0" />
            </div>
          </div>

          {/* Glosa */}
          <div className="form-group">
            <label>Glosa</label>
            <textarea
              name="Glosa"
              value={formData.Glosa}
              onChange={handleChange}
              rows="3"
              placeholder="Descripción del documento..."
            />
          </div>

          {/* Imágenes actuales */}
          {documento.imagenes && documento.imagenes.length > 0 && (
            <div className="form-group">
              <label>Imágenes Actuales</label>
              <div className="imagenes-actuales">
                {documento.imagenes.map(imagen => (
                  <div key={imagen.id} className="imagen-item">
                    <img
                      src={documentoService.getImagenUrl(imagen.ruta)}
                      alt="Documento"
                      onClick={() => window.open(documentoService.getImagenUrl(imagen.ruta), '_blank', 'noopener,noreferrer')}
                    />
                    <button
                      type="button"
                      className="btn-eliminar-imagen"
                      onClick={() => handleEliminarImagen(imagen.id)}
                    >
                      <LuTrash2 /> Eliminar
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Agregar nuevas imágenes */}
          <div className="form-group">
            <label><LuFileImage /> Agregar Nuevas Imágenes</label>
            <input type="file" onChange={handleFileChange} multiple accept="image/*,.pdf" />
            {archivos.length > 0 && <small>{archivos.length} archivo(s) seleccionado(s)</small>}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancelar" onClick={onClose} disabled={loading}>
              Cancelar
            </button>
            <button type="submit" className="btn-guardar" disabled={loading}>
              <LuSave />
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>

      <ConfirmModal
        isOpen={!!confirmEliminarImagen}
        title="Eliminar Imagen"
        message="¿Estás seguro de eliminar esta imagen? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        onConfirm={doEliminarImagen}
        onCancel={() => setConfirmEliminarImagen(null)}
        danger
      />
    </div>
  );
};

export default EditarDocumentoModal;
